import { useRef, useState } from "react";
import { FileSpreadsheet, FileUp, ShieldCheck } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export function UploadPanel({ onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const response = await api.post("/admin/imports", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (event.total) setProgress(Math.round((event.loaded / event.total) * 100));
        },
      });
      toast.success("Vendor file uploaded. Extraction is running.");
      onUploaded(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl" data-testid="vendor-upload-dropzone">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-blue-300">
            <ShieldCheck className="h-4 w-4" /> Private vendor ingestion
          </div>
          <h2 className="font-headline text-2xl font-semibold text-white">Upload PDF or CSV price list</h2>
          <p className="mt-1 text-sm text-slate-400">Source files, vendor identity, and base wholesale costs remain admin-only.</p>
        </div>
        <button
          type="button"
          data-testid="file-upload-button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="dropzone flex min-h-32 min-w-72 flex-col items-center justify-center rounded-2xl border border-dashed border-blue-400/50 bg-blue-500/10 px-6 py-5 text-blue-200 transition-colors hover:border-blue-300 hover:bg-blue-500/15 disabled:opacity-60"
        >
          {uploading ? <FileSpreadsheet className="mb-2 h-8 w-8 animate-pulse" /> : <FileUp className="mb-2 h-8 w-8" />}
          <span className="text-sm font-semibold">{uploading ? "Uploading and staging..." : "Choose vendor file"}</span>
          <span className="mt-1 text-xs text-slate-400">PDF or CSV, up to 25MB</span>
        </button>
        <input
          ref={inputRef}
          data-testid="file-upload-input"
          type="file"
          accept=".pdf,.csv,application/pdf,text/csv"
          className="hidden"
          onChange={(event) => upload(event.target.files?.[0])}
        />
      </div>
      {uploading ? (
        <div className="mt-4" data-testid="upload-progress-indicator">
          <div className="h-2 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full bg-blue-500 transition-[width]" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-400">Upload {progress}% complete. AI extraction starts immediately after upload.</p>
        </div>
      ) : null}
    </section>
  );
}
